import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useGetAdminBusinessUnitsQuery } from 'services/api/adminBusinessUnits.api';
import type { ConversationEval, MessageToEval } from 'services/types/conversationEvals';

interface ViewConversationEvalProps {
  conversationEval: ConversationEval;
}

const getFileExtension = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  return extension;
};

const isImageFile = (fileName: string): boolean => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  return imageExtensions.includes(getFileExtension(fileName));
};

const isDocumentFile = (fileName: string): boolean => {
  const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'rtf'];
  return documentExtensions.includes(getFileExtension(fileName));
};

const ViewConversationEval: React.FC<ViewConversationEvalProps> = ({ conversationEval }) => {
  const { data: businessUnits } = useGetAdminBusinessUnitsQuery(null);

  const getBusinessUnitNames = () => {
    if (!businessUnits) return [];

    return conversationEval.businessUnitIds.map((id) => {
      const businessUnit = businessUnits.find((bu) => bu.id === id);
      return businessUnit ? businessUnit.name : `Business Unit ${id}`;
    });
  };

  const getMessageStyle = (role: string) => {
    if (role === 'user') {
      return {
        backgroundColor: '#f0f4f8',
        alignSelf: 'flex-start',
        borderRadius: '12px 12px 12px 0',
      };
    }
    return {
      backgroundColor: '#e3f2fd',
      alignSelf: 'flex-end',
      borderRadius: '12px 12px 0 12px',
    };
  };

  const renderAttachment = (message: MessageToEval) => {
    const { fileUrl, fileName, mediaId } = message;

    if (!fileUrl && !fileName && !mediaId) {
      return null;
    }

    const displayFileName = fileName || (fileUrl ? fileUrl.split('/').pop() : 'Archivo adjunto');
    const displayUrl = fileUrl || (mediaId ? `Media ID: ${mediaId}` : null);

    if (fileName && isImageFile(fileName)) {
      return (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography
            variant="caption"
            gutterBottom
            sx={{
              color: 'text.secondary',
              display: 'block',
            }}
          >
            📷 Imagen adjunta:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <img
              src={fileUrl || ''}
              alt={displayFileName}
              style={{
                maxWidth: '200px',
                maxHeight: '150px',
                borderRadius: '8px',
                border: '1px solid #ddd',
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {displayFileName}
          </Typography>
          {mediaId && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
              }}
            >
              Media ID: {mediaId}
            </Typography>
          )}
        </Box>
      );
    }

    if (fileName && isDocumentFile(fileName)) {
      return (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography
            variant="caption"
            gutterBottom
            sx={{
              color: 'text.secondary',
              display: 'block',
            }}
          >
            📄 Documento adjunto:
          </Typography>
          <Box
            sx={{
              p: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              borderRadius: 1,
              border: '1px solid #ddd',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {displayFileName}
            </Typography>
            {displayUrl && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                }}
              >
                URL: {displayUrl}
              </Typography>
            )}
            {mediaId && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                }}
              >
                Media ID: {mediaId}
              </Typography>
            )}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 1, mb: 1 }}>
        <Typography
          variant="caption"
          gutterBottom
          sx={{
            color: 'text.secondary',
            display: 'block',
          }}
        >
          📎 Archivo adjunto:
        </Typography>
        <Box
          sx={{
            p: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            borderRadius: 1,
            border: '1px solid #ddd',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {displayFileName}
          </Typography>
          {displayUrl && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
              }}
            >
              URL: {displayUrl}
            </Typography>
          )}
          {mediaId && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
              }}
            >
              Media ID: {mediaId}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Typography variant="h6" gutterBottom>
            Business Units
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {getBusinessUnitNames().map((name) => (
              <Chip key={name} label={name} />
            ))}
          </Box>
        </Grid>
        <Grid size={12}>
          <Divider />
        </Grid>
        <Grid size={12}>
          <Typography variant="h6" gutterBottom>
            Mock Client
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              maxHeight: '200px',
              overflow: 'auto',
            }}
          >
            <pre style={{ margin: 0 }}>{JSON.stringify(conversationEval.mockClient, null, 2)}</pre>
          </Box>
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid size={12}>
          <Typography variant="h6" gutterBottom>
            Conversación
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {conversationEval.messages.map((message) => (
              <Box
                key={`${message.text}-${message.role}-${message.toolCalls?.join('') || ''}`}
                sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
              >
                <Box
                  component={Paper}
                  elevation={1}
                  sx={{
                    p: 2,
                    maxWidth: '80%',
                    ...getMessageStyle(message.role),
                  }}
                >
                  <Typography
                    variant="caption"
                    gutterBottom
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                    }}
                  >
                    {message.role.toUpperCase()}
                  </Typography>

                  {renderAttachment(message)}

                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.text || JSON.stringify(message.toolCalls)}
                  </Typography>

                  {message.expected && (
                    <Box sx={{ mt: 2, p: 1, backgroundColor: 'rgba(255, 255, 255, 0.5)', borderRadius: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                        }}
                      >
                        Expected Response:
                      </Typography>
                      <Typography variant="body2">{message.expected.responseDescription}</Typography>
                      {message.expected.responseDistance && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                            mt: 1,
                          }}
                        >
                          Response Distance: {message.expected.responseDistance}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewConversationEval;
